"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Vector2 = void 0;
const _1 = require(".");
class Vector2 {
    constructor(x, y) {
        this.x = x;
        this.y = y;
    }
    /**
     * Creates a zero vector
     * @returns Vector [0,0]
     */
    static Zero() {
        return new Vector2(0, 0);
    }
    /**
     * Creates a one vector
     * @returns Vector [1,1]
     */
    static One() {
        return new Vector2(1, 1);
    }
    /**
     * Checks to see if Vector A equals Vector B
     * @param a Vector A
     * @param b Vector B
     * @returns True if Vector A === Vector B
     */
    static Equal(a, b) {
        if (a.x !== b.x)
            return false;
        if (a.y !== b.y)
            return false;
        return true;
    }
    /**
     * Creates a Vector from a Data Transfer Object
     * @param dto The Data Transfer Object
     * @returns The Vector
     */
    static FromDTO(dto) {
        return new Vector2(dto.x, dto.y);
    }
    /**
     * Adds Vector B to Vector A
     * @param a Vector A
     * @param b Vector B
     * @returns A + B
     */
    static Add(a, b) {
        return new Vector2(a.x + b.x, a.y + b.y);
    }
    /**
     * Subtracts Vector B from Vector A
     * @param a Vector A
     * @param b Vector B
     * @returns A - B
     */
    static Subtract(a, b) {
        return new Vector2(a.x - b.x, a.y - b.y);
    }
    /**
     * Get the magnitude (length) of the vector
     */
    get magnitued() {
        const xSqr = this.x * this.x;
        const ySqr = this.y * this.y;
        const magnitude = Math.sqrt(xSqr + ySqr);
        return magnitude;
    }
    /**
     * Get the unit vector
     */
    get unit() {
        const mag = this.magnitued;
        if (mag === 0) {
            return Vector2.Zero();
        }
        const unitX = this.x / mag;
        const unitY = this.y / mag;
        return new Vector2(unitX, unitY);
    }
    /**
     * Returns the angle of the direction of this angle in degrees.
     * A right vector [1,0] will return 0, a left vector [-1,0] will return 180, an up vector [0,1] will return 90 and a down vector [0,-1] will return -90
     */
    get theta() {
        return (Math.atan2(this.y, this.x) * 180) / Math.PI;
    }
    /**
     * Retuns the vector as an array of numbers [x,y]
     */
    get array() {
        return [this.x, this.y];
    }
    /**
     * Get the vector as a Data Transfer Object
     */
    get dto() {
        return { x: this.x, y: this.y };
    }
}
exports.Vector2 = Vector2;
/**
 * Checks to see if Vector A is close to Vector B
 * @param a Vector A
 * @param b Vector B
 * @param threshold Tolerance to define "close". Defaults to 0.01
 * @returns True if A and B are close enough
 */
Vector2.Close = (a, b, threshold = 0.01) => {
    const diff = Vector2.Subtract(a, b);
    const diffMag = diff.magnitued;
    if (diffMag < threshold)
        return true;
    else
        return false;
};
/**
 * Rotates a vector by an angle
 * @param vec The original vector
 * @param angle The angle
 * @returns A new Vector that has been rotated from the original by an angle
 */
Vector2.Rotate = (vec, angle) => {
    const x = vec.x * Math.cos(angle.radians) - vec.y * Math.sin(angle.radians);
    const y = vec.x * Math.sin(angle.radians) + vec.y * Math.cos(angle.radians);
    return new Vector2(x, y);
};
/**
 * Scales a vector uniformly
 * @param vector The original vector
 * @param scale Scale factor
 * @returns A new, scaled vector
 */
Vector2.Scale = (vector, scale) => {
    const x = scale * vector.x;
    const y = scale * vector.y;
    return new Vector2(x, y);
};
/**
 * Creates a new vector of a given lenght and a unit that is equal to the original vector
 * @param vector The original vector. This determines the unit of the final vector
 * @param length The desired length of the final vector
 * @returns The final vector
 */
Vector2.NewVectorOfLength = (vector, length) => {
    const unit = vector.unit;
    const x = length * unit.x;
    const y = length * unit.y;
    return new Vector2(x, y);
};
/**
 * Calculate the dot product between Vectors A and B
 * @param a Vector A
 * @param b Vector B
 * @returns The Dot product between A and B
 */
Vector2.Dot = (a, b) => {
    return a.x * b.x + a.y * b.y;
};
/**
 * Calculates the dot product between Vectors A and B
 * @param a Vector A
 * @param b Vector B
 * @returns A . B
 */
Vector2.AngleBetween = (a, b) => {
    const dot = Vector2.Dot(a, b);
    const magnitues = a.magnitued * b.magnitued;
    const angRadians = Math.acos(dot / magnitues);
    return _1.Angle.FromRadians(angRadians);
};
/**
 * Calculates the Cross product between Vectors A and B
 * @param a Vector A
 * @param b Vector B
 * @returns A X B
 */
Vector2.Cross = (a, b) => {
    return a.x * b.y - a.y * b.x;
};
